import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaSearch, FaSync, FaMagic, FaTimes, FaPlay, FaHourglassHalf } from 'react-icons/fa';
import axios from 'axios';
import { regenerateVideo } from './FileUploader';
import { QUIZMASTER_API } from '../config';
import PointsConfirmationModal from './PointsConfirmationModal';
import FloatingChat from './FloatingChat';
import '../styles/styles.css';

const VideoList = ({ accessToken }) => {
    const [videoData, setVideoData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [apiStatus, setApiStatus] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [costConfirmation, setCostConfirmation] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const location = useLocation();
    const navigate = useNavigate();
    const document = location.state?.selectedDocument;
    const costConfirmationResolveRef = useRef(null);

    const fetchVideos = useCallback(async ({ showSpinner = false } = {}) => {
        if (!document) return;
        if (showSpinner) setLoading(true);
        else setRefreshing(true);

        try {
            const response = await axios.get(
                `${QUIZMASTER_API}/videos/get-videos-by-document/?document_id=${document.id}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            const videos = response.data.videos || [];
            setVideoData(videos);

            const isAnyProcessing = videos.some(v => v.status === 'processing');
            if (isAnyProcessing) {
                setApiStatus('Video is rendering... please wait.');
            } else {
                setApiStatus('');
            }
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [document, accessToken]);

    useEffect(() => {
        fetchVideos({ showSpinner: true });
    }, [fetchVideos]);

    // Polling logic: Stops when no processing videos remain
    useEffect(() => {
        const needsPolling = videoData.some(v => v.status === 'processing') || isRegenerating;
        if (!needsPolling) return;

        const interval = setInterval(() => {
            fetchVideos({ showSpinner: false });
        }, 10000);

        return () => clearInterval(interval);
    }, [videoData, isRegenerating, fetchVideos]);

    const handleRegenerateVideo = async () => {
        if (!document) return;
        const durationMinutes = 1;

        try {
            await regenerateVideo({
                documentId: document.id,
                tokenCount: document.token_count || 1,
                filename: document.filename,
                setApiStatus,
                startTimer: () => { },
                stopTimer: () => { },
                setLoading: setIsRegenerating,
                refreshDocuments: () => fetchVideos({ showSpinner: false }),
                accessToken,
                durationMinutes,
                confirmCost: ({ estimatedPoints }) => new Promise(res => {
                    costConfirmationResolveRef.current = res;
                    setCostConfirmation({ estimatedPoints });
                })
            });
        } catch (err) {
            // This runs when user clicks Cancel in the points confirmation modal
            if (err.message === 'User canceled' || err.message.includes('canceled')) {
                setApiStatus('');                    // ← Clear the "Estimating..." message
                setIsRegenerating(false);            // ← Re-enable the regenerate button
                setCostConfirmation(null);           // ← Hide modal (already done, but safe)
            } else {
                console.error("Regeneration failed", err);
                setApiStatus('Video generation failed. Please try again.');
                setIsRegenerating(false);
            }
        }
    };

    if (loading) return <div className="loading-container"><div className="spinner"></div><p>Loading videos...</p></div>;

    return (
        <div className="scroll-container">
            <div className="document-list-wrapper podcast-list-wrapper">
                <div className="podcast-list-header">
                    <div className="podcast-list-header__titles">
                        <h2 className="sub-header">Videos for {document?.filename}</h2>
                        <p className="podcast-list-header__subtitle">Watch AI-generated videos based on your content.</p>
                    </div>
                </div>

                {(apiStatus || refreshing) && (
                    <div className="api-status-container">
                        <div className="spinner spinner--inline"></div>
                        <p className="api-status-text">{apiStatus || 'Refreshing list...'}</p>
                    </div>
                )}

                <div className="search-container podcast-list-search">
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            className="search-input search-input--large"
                            placeholder="Search videos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="clear-button" onClick={() => setSearchTerm('')}>
                                <FaTimes className="clear-button-icon" />
                            </button>
                        )}
                    </div>
                    <button
                        className="button button--icon podcast-regenerate-button"
                        onClick={handleRegenerateVideo}
                        disabled={isRegenerating}
                    >
                        <span className="podcast-regenerate-button__icon"><FaMagic /></span>
                        <span className="podcast-regenerate-button__badge">NEW</span>
                    </button>
                </div>

                <div className="document-grid podcast-grid">
                    {videoData
                        .filter(v => v.title.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((v) => {
                            const isProcessing = v.status === 'processing';
                            return (
                                <div
                                    key={v.video_id}
                                    className={`document-card podcast-card ${isProcessing ? 'processing' : ''}`}
                                    /* Corrected navigation to /video-page to match RootApp.js */
                                    onClick={() => !isProcessing && navigate('/video-page', { state: { selectedVideo: v, selectedDocument: document } })}
                                    style={{
                                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                                        opacity: isProcessing ? 0.7 : 1
                                    }}
                                >
                                    <div className="podcast-card__header">
                                        <h3 className="podcast-card__title">{v.title}</h3>
                                        {isProcessing ? <FaHourglassHalf className="spinner--inline" /> : <FaPlay className="document-card__icon" />}
                                    </div>
                                    <div className="podcast-card__meta">
                                        <span className={`status-badge ${v.status}`}>{v.status}</span>
                                    </div>
                                </div>
                            );
                        })}
                </div>

                <div className="podcast-list-footer">
                    <button className="button" onClick={() => navigate('/')}>
                        Back to Documents
                    </button>
                </div>
            </div>

            <PointsConfirmationModal
                visible={!!costConfirmation}
                estimatedPoints={costConfirmation?.estimatedPoints || 0}
                numQuestions={1}
                onConfirm={() => {
                    setCostConfirmation(null);
                    costConfirmationResolveRef.current?.(true);
                }}
                onCancel={() => {
                    setCostConfirmation(null);
                    costConfirmationResolveRef.current?.(false);
                    // ← Add these two lines:
                    setApiStatus('');
                    setIsRegenerating(false);
                }}
                title="Confirm Video Generation"
                questionText="video"
            />
            {document && <FloatingChat documentId={document.id} filename={document.filename} accessToken={accessToken} />}
        </div>
    );
};

export default VideoList;